import { useState, useRef } from 'react';
import JSZip from 'jszip';
import { Canvg } from 'canvg';
import { EnrichmentResult } from '../types';

export const useEnrichmentDownload = () => {
  const [csvData, setCsvData] = useState<string | null>(null);
  const [metadataContent, setMetadataContent] = useState<string | null>(null);
  const swarmRef = useRef<HTMLDivElement | null>(null);

  const generateCSV = (results: EnrichmentResult[]) => {
    if (!results || results.length === 0) return '';

    const headers = Object.keys(results[0]).join(',');
    const rows = results.map(result =>
      Object.values(result)
        .map(value => (typeof value === 'string' && value.includes(',') ? `"${value}"` : value))
        .join(','),
    );

    return [headers, ...rows].join('\n');
  };

  const convertSVGToPNG = async (svgElement: SVGElement): Promise<string> => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) throw new Error('Could not get canvas context');

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const canvg = Canvg.fromString(ctx, svgData);

    canvas.width = svgElement.getBoundingClientRect().width || 800;
    canvas.height = svgElement.getBoundingClientRect().height || 600;

    await canvg.render();

    return canvas.toDataURL('image/png');
  };

  const downloadZIP = async (csvData: string, metadata: string, searchType: string) => {
    try {
      const zip = new JSZip();

      if (csvData) {
        zip.file('enrichment_results.csv', csvData);
      }

      if (metadata) {
        zip.file('enrichment_metadata.txt', metadata);
      }

      const fileName = `enrichment_${searchType.toLowerCase()}.zip`;
      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating ZIP:', error);
      throw error;
    }
  };

  const downloadGraphPNG = async () => {
    try {
      if (!swarmRef.current) {
        throw new Error('Graph not found');
      }

      const svgElement = swarmRef.current.querySelector('svg');
      if (!svgElement) {
        throw new Error('Graph SVG not found');
      }

      const pngData = await convertSVGToPNG(svgElement);

      const link = document.createElement('a');
      link.href = pngData;
      link.download = 'enrichment_graph.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading graph:', error);
      throw error;
    }
  };

  return {
    csvData,
    setCsvData,
    metadataContent,
    setMetadataContent,
    swarmRef,
    generateCSV,
    downloadZIP,
    downloadGraphPNG,
  };
};
